/**
 * API configuration for backend connection
 */
import { getToken, setToken } from '../utils/tokenStore';

// In production (served by Flask), use relative URLs to avoid CORS issues
// In development (React dev server), use 127.0.0.1:5000 to avoid IPv6 issues with macOS ControlCenter
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? ''
  : 'http://127.0.0.1:5000';

export const API_ENDPOINTS = {
  // Auth endpoints
  LOGIN: `${API_BASE_URL}/api/auth/login`,
  REGISTER: `${API_BASE_URL}/api/auth/register`,
  VERIFY: `${API_BASE_URL}/api/auth/verify`,
  CURRENT_USER: `${API_BASE_URL}/api/auth/me`,
  LOGOUT: `${API_BASE_URL}/api/auth/logout`,
  REFRESH: `${API_BASE_URL}/api/auth/refresh`,

  // Workout endpoints
  WORKOUTS: `${API_BASE_URL}/api/workouts`,
  WORKOUT_BY_ID: (id) => `${API_BASE_URL}/api/workouts/${id}`,
  IMPORT_WORKOUTS: `${API_BASE_URL}/api/workouts/import`,
  SELECTIONS: (workoutId) => `${API_BASE_URL}/api/selections/${workoutId}`,
  CUSTOM_WORKOUTS: `${API_BASE_URL}/api/custom-workouts`,
  CUSTOM_WORKOUT_BY_ID: (id) => `${API_BASE_URL}/api/custom-workouts/${id}`,
  HEALTH: `${API_BASE_URL}/api/health`,
  VERSION: `${API_BASE_URL}/api/version`,
  STATS: `${API_BASE_URL}/api/stats`,
  EXPORT_TO_CALENDAR: `${API_BASE_URL}/api/export/calendar`,
  TRI_CLUB_SCHEDULE: `${API_BASE_URL}/api/tri-club-schedule`,
  WEEKLY_TARGETS: `${API_BASE_URL}/api/weekly-targets`,

  // Weather endpoints
  WEATHER: `${API_BASE_URL}/api/weather`,
  WEATHER_BY_DATE: (date) => `${API_BASE_URL}/api/weather/${date}`,
  WEATHER_BY_TIME_OF_DAY: (date) => `${API_BASE_URL}/api/weather/by-time-of-day/${date}`,

  // Location endpoints
  LOCATIONS: `${API_BASE_URL}/api/locations`,
};

/**
 * Attempt a silent token refresh via the httpOnly refresh cookie.
 * Returns the new access token string, or null on failure.
 */
async function attemptRefresh() {
  try {
    const response = await fetch(API_ENDPOINTS.REFRESH, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) return null;
    const data = await response.json();
    if (data.access_token) {
      setToken(data.access_token);
      return data.access_token;
    }
  } catch {
    // network error — cannot refresh
  }
  return null;
}

/**
 * Make an API call with the in-memory access token.
 * On 401, attempts a silent token refresh then retries once.
 *
 * @param {string} url - The API endpoint URL
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} - Fetch response
 */
export const apiCall = async (url, options = {}) => {
  const token = getToken();
  const { timeout = 5000, credentials, ...restOptions } = options;

  // Don't set Content-Type for FormData — let the browser set it with boundary
  const isFormData = restOptions.body instanceof FormData;

  const buildHeaders = (currentToken) => ({
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...restOptions.headers,
    ...(currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {}),
  });

  const controller = new AbortController();
  const signal = controller.signal;
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  const fetchOptions = {
    ...restOptions,
    headers: buildHeaders(token),
    signal,
    ...(credentials ? { credentials } : {}),
  };

  let response;
  try {
    response = await fetch(url, fetchOptions);
  } finally {
    clearTimeout(timeoutId);
  }

  // On 401, try to refresh once (but don't retry refresh/login calls to avoid loops)
  const isAuthCall = url === API_ENDPOINTS.REFRESH || url === API_ENDPOINTS.LOGIN;
  if (response.status === 401 && !isAuthCall) {
    const newToken = await attemptRefresh();
    if (newToken) {
      const retryController = new AbortController();
      const retryTimeoutId = setTimeout(() => retryController.abort(), timeout);
      try {
        return await fetch(url, {
          ...restOptions,
          headers: buildHeaders(newToken),
          signal: retryController.signal,
          ...(credentials ? { credentials } : {}),
        });
      } finally {
        clearTimeout(retryTimeoutId);
      }
    }
  }

  return response;
};

export default API_BASE_URL;
